import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, map, Observable, of, startWith, Subscription, switchMap } from 'rxjs';

import { BookingAppState } from '@fcom/common/interfaces/booking';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { ConfigService, unsubscribe } from '@fcom/core/index';
import { orderData } from '@fcom/common-booking/store';
import { FinnairCart, FinnairOrder } from '@fcom/dapi/api/models';
import { CartState } from '@fcom/dapi';
import { finShare } from '@fcom/rx';
import { getProductTripTypeFromBoundsForGA4 } from '@fcom/common/utils/gtm.utils';
import { GA4TripType } from '@fcom/common/interfaces';

import { cartState } from '../../../../store/selectors';

@Component({
  selector: 'fin-multi-city-survey',
  templateUrl: './multi-city-survey.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MultiCitySurveyComponent implements OnInit, OnDestroy {
  private configService = inject(ConfigService);
  private store$ = inject<Store<BookingAppState>>(Store);

  @Input() alwaysRender = false;

  survey = QualtricsSurveys.MULTI_CITY;
  openSurvey = false;
  enableSurvey$ = new BehaviorSubject(false);
  cartOrOrder$: Observable<FinnairOrder | FinnairCart>;

  private subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    const cartState$ = this.store$.pipe(cartState(), startWith<CartState>(null), finShare());
    const order$ = this.store$.pipe(orderData(), startWith<FinnairOrder>(null), finShare());

    this.cartOrOrder$ = order$.pipe(
      switchMap((order) => {
        return order ? of(order) : cartState$.pipe(map((state: CartState) => state?.cartData));
      })
    );

    this.checkIfSurveyShouldBeShown();
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  checkIfSurveyShouldBeShown(): void {
    if (!this.configService.cfg.enableMultiCity) {
      return;
    }

    if (this.alwaysRender) {
      this.enableSurvey$.next(true);
      return;
    }

    this.subscriptions.add(
      this.cartOrOrder$.subscribe((cartOrOrder) => {
        if (!cartOrOrder) {
          this.enableSurvey$.next(false);
          return;
        }

        const tripType = getProductTripTypeFromBoundsForGA4(cartOrOrder?.bounds);

        this.enableSurvey$.next(tripType === GA4TripType.MULTICITY);
      })
    );
  }
}
